import { useEffect, useState } from "react";
import {
  Dialog, AppBar, Toolbar, Typography, IconButton, Box, Snackbar, Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";

/**
 * Full-screen dialog that hosts the client posting-rules questionnaire
 * (public/posting-rules.html) inside an iframe. The questionnaire posts its
 * signed answers back via window.postMessage; we persist them to Convex.
 *
 * Same-origin: the HTML is served from the app's own /posting-rules.html, so
 * we validate event.origin === window.location.origin.
 */
export function PostingRulesDialog({
  open,
  onClose,
  submittedByEmail,
}: {
  open: boolean;
  onClose: () => void;
  submittedByEmail?: string;
}) {
  const submit = useMutation(api.postingRules.submit);
  const [snack, setSnack] = useState<{ sev: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const data = e.data;
      if (!data || data.type !== "altelix-posting-rules:submit") return;
      try {
        await submit({ payload: data.payload, submittedByEmail });
        setSnack({ sev: "success", msg: "Posting rules submitted — saved to the server." });
      } catch (err) {
        setSnack({
          sev: "error",
          msg: "Could not save to the server. Please use “Download .json” and send the file.",
        });
        // eslint-disable-next-line no-console
        console.error("posting-rules submit failed", err);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open, submit, submittedByEmail]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        slotProps={{ backdrop: { sx: { backgroundColor: "rgba(8,12,22,0.7)" } } }}
      >
        <AppBar position="relative" elevation={0} sx={{ bgcolor: "#15539e" }}>
          <Toolbar variant="dense">
            <RuleFolderIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>
              Posting Rules — Business Decisions
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, minHeight: 0, bgcolor: "#eef3f9" }}>
          {open && (
            <Box
              component="iframe"
              src="/posting-rules.html"
              title="Posting Rules Questionnaire"
              sx={{ width: "100%", height: "100%", border: "none", display: "block" }}
            />
          )}
        </Box>
      </Dialog>
      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
